import type { CourierBoard, Order, Statement } from "../../api/types";
import { t } from "../../i18n";
import { formatEuro } from "../../lib/money";
import { formatTime } from "../../lib/time";
import { paymentOf } from "../orders/order-view";

export type BoardDelivery = CourierBoard["deliveries"][number];

/** A courier's column on the board (prototype ccol). */
export interface CourierColumn {
  courier: CourierBoard["couriers"][number];
  /** His deliveries still to deliver, the oldest first. */
  rides: BoardDelivery[];
  /** Delivered and not settled yet; null when there is nothing. */
  statement: Statement | null;
}

/** "Te leveren" (nobody has them yet) and a column per courier, as the prototype draws the board. */
export function boardColumns(board: CourierBoard): { open: BoardDelivery[]; columns: CourierColumn[] } {
  return {
    open: board.deliveries.filter((order) => order.courier === null),
    columns: board.couriers.map((courier) => ({
      courier,
      rides: board.deliveries.filter((order) => order.courier?.id === courier.id),
      statement: board.unsettled.find((statement) => statement.courier.id === courier.id) ?? null,
    })),
  };
}

/** "🛵 onderweg (2)", "klaar om te vertrekken" or "in de zaak", and " · ingelogd in app" (prototype). */
export function courierStatus(column: CourierColumn): string {
  const text = t.couriers;
  const onTheWay = column.rides.filter((order) => order.departedAt !== null).length;
  // everything delivered and on his way back: when he is at the shop, to make his next orders ready
  const back = column.courier.backAt ? text.returning(formatTime(column.courier.backAt)) : null;
  const where = onTheWay > 0 ? text.onTheWay(onTheWay) : (back ?? (column.rides.length > 0 ? text.ready : text.inShop));
  return column.courier.signedIn ? `${where}${text.signedIn}` : where;
}

/** "Te innen € 19,20", or "Betaald · Bancontact" when nothing is due any more (prototype payBox). */
export function payPill(order: Order): { tone: "ok" | "warn"; text: string } {
  const due = order.totalCents - order.paidCents;
  return due > 0
    ? { tone: "warn", text: t.couriers.due(formatEuro(due)) }
    : { tone: "ok", text: t.couriers.paid(paymentOf(order)) };
}

/** "± 7,8 km"; nothing while the distance of the street is unknown. */
export function kmText(km: number | null): string | null {
  return km === null ? null : t.couriers.km(km.toLocaleString("nl-BE", { maximumFractionDigits: 1 }));
}
